import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Copy, RefreshCw, Bell, Smartphone, Calendar as CalIcon, CheckCircle2 } from "lucide-react";
import { api, API_ROOT, formatApiError } from "../../api";
import { useAuth } from "../../auth";
import { ensurePushSubscription, disablePushSubscription, sendPushTest } from "../../lib/push";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export default function Settings({ calendarToken, onTokenChange }) {
  const { user } = useAuth();
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [notifStatus, setNotifStatus] = useState("Notification" in window ? Notification.permission : "unsupported");
  const [pushOn, setPushOn] = useState(false);

  useEffect(() => {
    (async () => {
      if (!("serviceWorker" in navigator)) return;
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushOn(!!sub);
      } catch (_e) {}
    })();
  }, []);

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) return toast.error("Le nouveau mot de passe doit faire au moins 6 caractères");
    if (pwd.new_password !== pwd.confirm) return toast.error("Les mots de passe ne correspondent pas");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Mot de passe mis à jour");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const calendarUrl = calendarToken ? `${API_ROOT}/api/calendar/${calendarToken}.ics` : "";
  const webcal = calendarUrl.replace(/^https?:\/\//, "webcal://");

  const copy = (text, label = "Lien") => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié`);
  };

  const regenerate = async () => {
    if (!window.confirm("Régénérer le lien iCal ? L'ancien lien sera invalidé.")) return;
    try {
      const { data } = await api.post("/admin/calendar/regenerate");
      onTokenChange?.(data.calendar_token);
      toast.success("Nouveau lien généré");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const enableNotifs = async () => {
    if (!("Notification" in window)) return toast.error("Non supporté");
    const perm = await Notification.requestPermission();
    setNotifStatus(perm);
    if (perm === "granted") {
      const r = await ensurePushSubscription();
      if (r.ok) { setPushOn(true); toast.success("Notifications activées — vous recevrez les alertes même l'app fermée."); }
      else toast.message("Notifications activées (uniquement quand l'onglet est ouvert)", { description: r.reason });
    }
  };

  const disablePush = async () => {
    await disablePushSubscription();
    setPushOn(false);
    toast.success("Notifications push désactivées");
  };

  const testNotif = async () => {
    if (Notification.permission !== "granted") return toast.error("Activez d'abord les notifications");
    if (pushOn) {
      try { await sendPushTest(); toast.success("Push test envoyé"); return; } catch { /* fall through */ }
    }
    try {
      if (navigator.serviceWorker?.ready) {
        navigator.serviceWorker.ready.then((r) => r.showNotification("Notification test", { body: "Vous recevrez ce type d'alerte sur nouveau message.", icon: "/icon-192.png" }));
      } else {
        new Notification("Notification test", { body: "Vous recevrez ce type d'alerte sur nouveau message.", icon: "/icon-192.png" });
      }
    } catch (_e) {}
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="settings">
      <div className="rounded-lg border border-white/10 bg-[#0f0f0f] p-6">
        <div className="flex items-center gap-2 text-white"><KeyRound size={18} className="text-[#ff4d4d]" /><h3 className="font-head text-lg font-medium uppercase tracking-tight">Mot de passe</h3></div>
        <p className="mt-1 text-xs text-white/50">Connecté en tant que <span className="text-white">{user?.email}</span></p>
        <form onSubmit={changePwd} className="mt-5 space-y-4" data-testid="change-password-form">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/70">Mot de passe actuel</Label>
            <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} required className="border-white/10 bg-black/50" data-testid="current-password" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/70">Nouveau mot de passe</Label>
            <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} required minLength={6} className="border-white/10 bg-black/50" data-testid="new-password" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/70">Confirmer</Label>
            <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} required minLength={6} className="border-white/10 bg-black/50" data-testid="confirm-password" />
          </div>
          <button type="submit" disabled={saving} className="rounded-full bg-brand-gradient px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="save-password-btn">
            {saving ? "Mise à jour…" : "Mettre à jour"}
          </button>
        </form>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0f0f0f] p-6">
        <div className="flex items-center gap-2 text-white"><CalIcon size={18} className="text-[#ff4d4d]" /><h3 className="font-head text-lg font-medium uppercase tracking-tight">Sync Calendrier</h3></div>
        <p className="mt-1 text-xs text-white/50">Ajoutez vos rendez-vous à l'agenda de votre téléphone (iPhone / Android). Mise à jour automatique.</p>

        <div className="mt-5 space-y-3">
          <Label className="text-xs uppercase tracking-wider text-white/70">Lien iCal</Label>
          <div className="flex items-center gap-2">
            <Input readOnly value={calendarUrl} className="border-white/10 bg-black/50 text-xs" data-testid="ical-url" />
            <button onClick={() => copy(calendarUrl, "Lien iCal")} className="rounded-md border border-white/10 p-2 text-white hover:bg-white/5"><Copy size={14} /></button>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={webcal} className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-4 py-2 text-xs font-semibold text-white hover:scale-[1.02] transition-transform" data-testid="ical-subscribe-btn"><Smartphone size={14} />S'abonner sur mon téléphone</a>
            <button onClick={regenerate} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs text-white hover:bg-white/5" data-testid="ical-regen-btn"><RefreshCw size={14} />Régénérer</button>
          </div>
          <p className="text-[11px] leading-relaxed text-white/40">
            iPhone : Réglages › Calendrier › Comptes › Ajouter un compte › Autre › Compte CalDAV / <em>collez ce lien</em>.<br />
            Android : Google Agenda › Ajouter un autre agenda › Depuis une URL.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0f0f0f] p-6 lg:col-span-2">
        <div className="flex items-center gap-2 text-white"><Bell size={18} className="text-[#ff4d4d]" /><h3 className="font-head text-lg font-medium uppercase tracking-tight">Notifications natives</h3></div>
        <p className="mt-1 text-xs text-white/50">Recevez une alerte instantanée à chaque nouvelle demande ou rendez-vous, y compris quand le PWA est installé.</p>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-black/40 px-3 py-1.5 text-xs">
            État :
            {notifStatus === "granted" && <span className="flex items-center gap-1 text-green-400"><CheckCircle2 size={12} />Activées</span>}
            {notifStatus === "denied" && <span className="text-red-400">Refusées</span>}
            {notifStatus === "default" && <span className="text-yellow-300">Non demandées</span>}
            {notifStatus === "unsupported" && <span className="text-white/50">Non supporté</span>}
          </span>
          <span className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-black/40 px-3 py-1.5 text-xs">
            Push serveur :
            {pushOn ? <span className="flex items-center gap-1 text-green-400"><CheckCircle2 size={12} />Actif</span> : <span className="text-white/50">Inactif</span>}
          </span>
          {notifStatus !== "granted" && notifStatus !== "unsupported" && (
            <button onClick={enableNotifs} className="rounded-full bg-brand-gradient px-4 py-2 text-xs font-semibold text-white" data-testid="enable-notifs-btn">Activer les notifications</button>
          )}
          {notifStatus === "granted" && !pushOn && (
            <button onClick={enableNotifs} className="rounded-full bg-brand-gradient px-4 py-2 text-xs font-semibold text-white" data-testid="enable-push-btn">Activer push serveur</button>
          )}
          {pushOn && (
            <button onClick={disablePush} className="rounded-full border border-white/20 px-4 py-2 text-xs text-white hover:bg-white/5">Désactiver push</button>
          )}
          {notifStatus === "granted" && (
            <button onClick={testNotif} className="rounded-full border border-white/20 px-4 py-2 text-xs text-white hover:bg-white/5" data-testid="test-notif-btn">Envoyer une notification test</button>
          )}
        </div>
        <p className="mt-4 text-[11px] text-white/40">
          Astuce : installez l'app (icône « Installer ») pour recevoir les notifications même quand l'onglet est fermé. Le push serveur (VAPID) déclenche les alertes en temps réel via le service worker.
        </p>
      </div>
    </div>
  );
}
